"use client"

import { useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import emailjs from "@emailjs/browser"
import "./ContactSection.scss"
import Separador from "../Separador"

const ContactSection = () => {
  const formRef = useRef(null)

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    userType: "",
    comments: "",
    files: null,
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Popups
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)

  // ✅ REEMPLAZÁ con tus valores reales de EmailJS
  // (EmailJS Dashboard > Email Services / Email Templates / Account)
  const EMAILJS_SERVICE_ID = "service_34dafqa"
  const EMAILJS_TEMPLATE_ID = "template_4tp9rae"
  const EMAILJS_PUBLIC_KEY = "i_zhCjMS6NxaNBuGs"

  const userTypeOptions = [
    { value: "", label: "Selecciona una opción" },
    { value: "arquitecto", label: "Soy arquitecto" },
    { value: "desarrollador", label: "Soy desarrollador" },
    { value: "cliente-final", label: "Soy cliente final" },
  ]

  const filesNames = useMemo(() => {
    if (!formData.files || formData.files.length === 0) return ""
    return Array.from(formData.files)
      .map((f) => f.name)
      .join(", ")
  }, [formData.files])

  const handleInputChange = (e) => {
    const { name, value, files } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: files ? files : value,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.fullName.trim()) {
      newErrors.fullName = "El nombre completo es requerido"
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "El teléfono es requerido"
    }

    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "El email no es válido"
    }

    if (!formData.userType) {
      newErrors.userType = "Por favor selecciona una opción"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setFormData({
      fullName: "",
      phone: "",
      email: "",
      userType: "",
      comments: "",
      files: null,
    })

    // Limpia el input file visualmente (porque es uncontrolled)
    if (formRef.current) formRef.current.reset()
  }

  const openSuccessPopup = () => {
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 3500)
  }

  const openErrorPopup = () => {
    setShowError(true)
    setTimeout(() => setShowError(false), 4500)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    setShowSuccess(false)
    setShowError(false)

    try {
      // Importante: sendForm toma los inputs del form (incluye archivos)
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: EMAILJS_PUBLIC_KEY }
      )

      if (window.fbq) {
        window.fbq("track", "Lead", {
          content_name: "Formulario Contacto Norlux",
          page_path: "/contacto",
          user_type: formData.userType || undefined,
        })
      }

      setIsSubmitting(false)
      openSuccessPopup()
      resetForm()
    } catch (err) {
      console.error("EmailJS error:", err)
      setIsSubmitting(false)
      openErrorPopup()
    }
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <section className="contact-section">
      <div className="contact-container">
        <motion.div
          className="contact-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="contact-header" variants={itemVariants}>
            <h2 className="section-title">
              Hablemos de <b>tu proyecto</b>
            </h2>
            <Separador color="--color-3" width="100px" />
            <p className="mt-4">
              Contanos sobre tu proyecto y te acompañamos desde el primer momento. Nuestro equipo de especialistas está
              listo para asesorarte.
            </p>
          </motion.div>

          <motion.form
            ref={formRef}
            className="contact-form"
            onSubmit={handleSubmit}
            variants={itemVariants}
          >
            {/* Campo extra para mandar nombres de archivos en el mail */}
            <input type="hidden" name="files_names" value={filesNames} />

            <div className="form-grid">
              <motion.div className="form-group" variants={itemVariants}>
                <label htmlFor="fullName">Nombre Completo *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={errors.fullName ? "error" : ""}
                  placeholder="Ingresa tu nombre completo"
                />
                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label htmlFor="phone">Teléfono *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? "error" : ""}
                  placeholder="Ej: +54 11 1234-5678"
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? "error" : ""}
                  placeholder="tu@email.com"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </motion.div>

              <motion.div className="form-group" variants={itemVariants}>
                <label htmlFor="userType">Tipo de cliente *</label>
                <select
                  id="userType"
                  name="userType"
                  value={formData.userType}
                  onChange={handleInputChange}
                  className={errors.userType ? "error" : ""}
                >
                  {userTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.userType && <span className="error-message">{errors.userType}</span>}
              </motion.div>
            </div>

            <motion.div className="form-group full-width" variants={itemVariants}>
              <label htmlFor="files">Archivos adjuntos</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="files"
                  name="files"
                  onChange={handleInputChange}
                  multiple
                  accept=".pdf,.doc,.docx,.webp,.webp,.webp,.dwg"
                />
                <div className="file-input-display">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" />
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" />
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" />
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>
                    {formData.files && formData.files.length > 0
                      ? `${formData.files.length} archivo(s) seleccionado(s)`
                      : "Seleccionar archivos (PDF, DOC, JPG, DWG)"}
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div className="form-group full-width" variants={itemVariants}>
              <label htmlFor="comments">Comentarios</label>
              <textarea
                id="comments"
                name="comments"
                value={formData.comments}
                onChange={handleInputChange}
                placeholder="Contanos sobre tu proyecto, necesidades específicas, plazos, etc."
                rows="5"
              />
            </motion.div>

            <motion.div className="form-actions" variants={itemVariants}>
              <motion.button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar consulta
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </>
                )}
              </motion.button>

              <p className="form-note">Te responderemos en menos de 24 horas con una propuesta personalizada.</p>
            </motion.div>
          </motion.form>
        </motion.div>
      </div>

      {/* ✅ POPUPS */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            className="popup-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="popup-card success"
              initial={{ opacity: 0, y: 15, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.98 }}
              transition={{ duration: 0.25 }}
            >
              <div className="popup-icon">✓</div>
              <div className="popup-text">
                <h4>¡Enviado con éxito!</h4>
                <p>Gracias por tu consulta. Te respondemos a la brevedad.</p>
              </div>
              <button className="popup-close" onClick={() => setShowSuccess(false)} aria-label="Cerrar">
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}

        {showError && (
          <motion.div
            className="popup-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="popup-card error"
              initial={{ opacity: 0, y: 15, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.98 }}
              transition={{ duration: 0.25 }}
            >
              <div className="popup-icon">!</div>
              <div className="popup-text">
                <h4>No se pudo enviar</h4>
                <p>Reintentá en unos minutos o escribinos por WhatsApp.</p>
              </div>
              <button className="popup-close" onClick={() => setShowError(false)} aria-label="Cerrar">
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default ContactSection
